import GeoHandler from 'handlers/geo.handler';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { type GeoTrip } from 'types/geo.types';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import FleetPlanningHandler from 'handlers/fleet-planning.handler';
import FleetPlanningAPI, {
  type LatestApprovedDispatchPlan,
} from 'api/fleet-planning.api';
import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Box } from '@mui/material';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { Timeline } from './components/TimeLine';
import { KPICardAndChartWrapper } from './components/KPICardAndChartWrapper';
import { ActivePlanCard } from './components/ActivePlanCard';
import { HeaderWithFilters } from './components/HeaderWithFilters';
import { formatStartEndDateUTC } from 'utils/helpers/dateTime';
import useCustomerInfo from 'hooks/useCustomerInfo';
import {
  formatCustomerShiftLabel,
  getCustomerShiftsUTCForDate,
  getCurrentCustomerShiftInfo,
} from 'utils/helpers/customerShiftHelpers';
import { HourlyMaterialComparisonChart } from './components/ActualVsActiveComparsion';
import { useTranslation } from 'react-i18next';
import DispatchPlanMap from './components/DispatchPlanMap';
import useAppTheme from 'hooks/useAppTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

export const DispatchSummary = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const [tripsData, setTripsData] = useState<Record<string, GeoTrip[]>>({});
  const assetsData = useAppSelector((state) => state.assetReducer.assets);
  const loadingPoints = useAppSelector(
    (state) => state.siteConfigReducer.loadingPoints
  );
  const dumpingPoints = useAppSelector(
    (state) => state.siteConfigReducer.dumpingPoints
  );
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const geoHandler = new GeoHandler();
  const fleetPlanningHandler = new FleetPlanningHandler();
  const fleetPlanningApi = new FleetPlanningAPI();
  const dispatch = useAppDispatch();
  const { customerTz, customerCode } = useCustomerInfo();
  const [loading, setLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState('Today');
  const [latestPlan, setLatestPlan] =
    useState<LatestApprovedDispatchPlan | null>(null);

  const yesterdayInTz = useMemo(() => {
    return dayjs().tz(customerTz).subtract(1, 'day');
  }, [customerTz]);

  const [date, setDate] = useState(yesterdayInTz);

  const [currentShiftNumber, setCurrentShiftNumber] = useState<number>();
  const [currentShiftLabel, setCurrentShiftLabel] = useState<string>();
  const [currentShiftStartUtc, setCurrentShiftStartUtc] = useState<string>();
  const [currentShiftEndUtc, setCurrentShiftEndUtc] = useState<string>();
  const [isShiftContextReady, setIsShiftContextReady] = useState(false);

  const [availableShifts, setAvailableShifts] = useState<
    Array<{ shiftNumber: number; label: string }>
  >([]);
  const [selectedShiftNumber, setSelectedShiftNumber] = useState<number>();

  const ALL_AVAILABLE_ASSETS = useMemo(() => {
    if (Array.isArray(assetsData)) {
      return assetsData
        .filter((asset) => asset?.assetType === 'haul_truck')
        .map((asset) => ({
          id: asset?.assetId,
          name: asset?.bumperNumber,
          deviceId: asset?.device?.deviceId,
        }));
    }
    return [];
  }, [assetsData]);

  const fetchTripsData = async (
    paramsStartTime: string,
    paramsEndDateTime: string
  ) => {
    try {
      const deviceIdToBumperMap = ALL_AVAILABLE_ASSETS.reduce<
        Record<string, string>
      >((acc, asset) => {
        if (asset.deviceId && asset.name) {
          acc[asset.deviceId] = asset.name;
        }
        return acc;
      }, {});

      const deviceIds = ALL_AVAILABLE_ASSETS.map(
        (asset) => asset.deviceId
      ).filter(Boolean);

      if (deviceIds.length === 0) return;

      const gpsStartDateTime = dayjs
        .utc(paramsStartTime)
        .format('YYYY-MM-DDTHH:mm:ss[Z]');
      const gpsEndDateTime = dayjs
        .utc(paramsEndDateTime)
        .format('YYYY-MM-DDTHH:mm:ss[Z]');

      // Run all calls in parallel
      const tripsResults = await Promise.allSettled(
        deviceIds.map(async (deviceId) => {
          const response = await geoHandler.getTrips(
            deviceId,
            gpsStartDateTime,
            gpsEndDateTime
          );
          return { deviceId, data: response?.data };
        })
      );

      // Build a clean dictionary keyed by deviceId
      const formattedTrips: Record<string, any> = {};
      tripsResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          const bumperNumber = deviceIdToBumperMap[result.value.deviceId];
          formattedTrips[bumperNumber] = result?.value?.data || [];
        } else {
          console.error(
            `Failed for device ${result.reason?.deviceId}`,
            result.reason
          );
        }
      });

      setTripsData(formattedTrips);
    } catch (error) {
      console.error('Error fetching trips:', error);
    }
  };

  const fetchActivePlan = async (
    paramsStartDateTime: string,
    paramsEndDateTime: string,
    paramSelectedRange: string
  ) => {
    try {
      const planDate = dayjs.utc(paramsStartDateTime).format('YYYY-MM-DD');
      await fleetPlanningHandler.getDispatchPlanSummaryByDay(
        paramsStartDateTime,
        paramsEndDateTime,
        planDate,
        true,
        'single_active'
      );
    } catch (error) {
      console.error(error);
    }
  };

  const fetchLatestApprovedPlan = async () => {
    if (!customerId) return;
    try {
      const plan = await fleetPlanningApi.getLatestApprovedDispatchPlan(
        customerId
      );
      setLatestPlan(plan);
    } catch (error) {
      console.error('Failed to fetch latest approved dispatch plan', error);
    }
  };

  const fetchData = async (
    paramsStartDate: string,
    paramsEndDate: string,
    shouldFetchActivePlan: boolean,
    paramSelectedRange: string
  ) => {
    try {
      setLoading(true);

      const reqArr = [fetchTripsData(paramsStartDate, paramsEndDate)];

      if (shouldFetchActivePlan) {
        reqArr.push(
          fetchActivePlan(paramsStartDate, paramsEndDate, paramSelectedRange)
        );
      }
      await Promise.all(reqArr);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getShiftWindowForDate = (
    baseDate: Dayjs,
    shiftNumber: number
  ): {
    startUtcISO: string;
    endUtcISO: string;
  } | null => {
    if (!customerCode || !customerTz || !shiftNumber) return null;

    const dateStr = baseDate.tz(customerTz).format('YYYY-MM-DD');
    const shiftsForDate = getCustomerShiftsUTCForDate(customerCode, dateStr);

    if (
      !Array.isArray(shiftsForDate) ||
      shiftNumber < 1 ||
      shiftNumber > shiftsForDate.length
    ) {
      return null;
    }

    const { startHour, endHour } = shiftsForDate[shiftNumber - 1];
    const normalizedEndHour = endHour <= startHour ? endHour + 24 : endHour;

    const customerDayStart = dayjs.tz(`${dateStr}T00:00:00`, customerTz);
    const startUtcISO = customerDayStart.add(startHour, 'hour').toISOString();

    const endUtcISO = customerDayStart
      .add(normalizedEndHour, 'hour')
      .toISOString();

    return { startUtcISO, endUtcISO };
  };

  const handleDateChange = async (
    range: string,
    date: Dayjs | null,
    shouldFetchActivePlan: boolean,
    isButtonClick?: boolean,
    options?: {
      shiftNumberOverride?: number;
    }
  ) => {
    const effectiveShiftNumber =
      options?.shiftNumberOverride ?? selectedShiftNumber ?? currentShiftNumber;

    // For Today / 1D we want to focus on the current shift only
    if (range === 'Today' || range === '1D') {
      if (currentShiftStartUtc && currentShiftEndUtc) {
        await fetchData(
          currentShiftStartUtc,
          currentShiftEndUtc,
          shouldFetchActivePlan,
          range
        );
      } else {
        const { startUtcISO, endUtcISO } = formatStartEndDateUTC(
          range,
          date,
          date,
          customerTz
        );

        if (!startUtcISO || !endUtcISO) return;
        await fetchData(startUtcISO, endUtcISO, shouldFetchActivePlan, range);
      }

      setSelectedDateRange(range);
      setDate(yesterdayInTz);
      return;
    }

    // For non-Today ranges, allow selecting any shift
    const baseDate =
      range === '1D'
        ? dayjs().tz(customerTz).subtract(1, 'day')
        : date ?? dayjs().tz(customerTz).subtract(1, 'day');

    if (effectiveShiftNumber) {
      const window = getShiftWindowForDate(baseDate, effectiveShiftNumber);
      if (window) {
        await fetchData(
          window.startUtcISO,
          window.endUtcISO,
          shouldFetchActivePlan,
          range
        );
        setSelectedDateRange(range);
        setDate(baseDate);
        setSelectedShiftNumber(effectiveShiftNumber);
        return;
      }
    }

    // Fallback to existing date-range behavior
    const { startUtcISO, endUtcISO } = formatStartEndDateUTC(
      range,
      date,
      date,
      customerTz
    );

    if (!startUtcISO || !endUtcISO) return;

    if (range === 'Custom') {
      if (isButtonClick) {
        await fetchData(startUtcISO, endUtcISO, shouldFetchActivePlan, range);
      }
    } else {
      await fetchData(startUtcISO, endUtcISO, shouldFetchActivePlan, range);
    }
    setSelectedDateRange(range);

    if (date) setDate(date);
  };

  useEffect(() => {
    setIsShiftContextReady(false);
    if (!customerCode || !customerTz) {
      setAvailableShifts([]);
      setCurrentShiftNumber(undefined);
      setCurrentShiftLabel(undefined);
      setCurrentShiftStartUtc(undefined);
      setCurrentShiftEndUtc(undefined);
      setSelectedShiftNumber(undefined);
      setIsShiftContextReady(true);
      return;
    }

    const todayInCustomerTz = dayjs().tz(customerTz);
    const todayDateStr = todayInCustomerTz.format('YYYY-MM-DD');
    const currentShiftInfo = getCurrentCustomerShiftInfo(
      customerCode,
      customerTz
    );
    const shiftsForToday = getCustomerShiftsUTCForDate(
      customerCode,
      todayDateStr
    );
    const newAvailableShifts: Array<{ shiftNumber: number; label: string }> =
      [];

    if (Array.isArray(shiftsForToday) && shiftsForToday.length > 0) {
      shiftsForToday.forEach((shift, index) => {
        const normalizedEndHour =
          shift.endHour <= shift.startHour ? shift.endHour + 24 : shift.endHour;
        const customerDayStart = dayjs.tz(
          `${todayDateStr}T00:00:00`,
          customerTz
        );
        const startLocal = customerDayStart
          .add(shift.startHour, 'hour')
          .format('HH:mm');
        const endLocal = customerDayStart
          .add(normalizedEndHour, 'hour')
          .format('HH:mm');

        newAvailableShifts.push({
          shiftNumber: index + 1,
          label: formatCustomerShiftLabel(index, startLocal, endLocal),
        });
      });
    }

    setAvailableShifts(newAvailableShifts);

    if (
      currentShiftInfo.currentShiftNumber &&
      shiftsForToday[currentShiftInfo.currentShiftNumber - 1]
    ) {
      const { startHour, endHour } =
        shiftsForToday[currentShiftInfo.currentShiftNumber - 1];
      const normalizedEndHour = endHour <= startHour ? endHour + 24 : endHour;
      const customerDayStart = dayjs.tz(`${todayDateStr}T00:00:00`, customerTz);

      const startUtc = customerDayStart.add(startHour, 'hour').toISOString();
      const endUtc = customerDayStart
        .add(normalizedEndHour, 'hour')
        .toISOString();

      setCurrentShiftNumber(currentShiftInfo.currentShiftNumber);
      setCurrentShiftLabel(currentShiftInfo.currentShiftLabel);
      setCurrentShiftStartUtc(startUtc);
      setCurrentShiftEndUtc(endUtc);

      if (!selectedShiftNumber) {
        setSelectedShiftNumber(currentShiftInfo.currentShiftNumber);
      }
    } else {
      setCurrentShiftNumber(undefined);
      setCurrentShiftLabel(undefined);
      setCurrentShiftStartUtc(undefined);
      setCurrentShiftEndUtc(undefined);
      setSelectedShiftNumber(undefined);
    }
    setIsShiftContextReady(true);
  }, [customerCode, customerTz]);

  useEffect(() => {
    if (
      !customerTz ||
      ALL_AVAILABLE_ASSETS.length === 0 ||
      !isShiftContextReady
    ) {
      return;
    }
    void handleDateChange('Today', null, true);
  }, [ALL_AVAILABLE_ASSETS, customerTz, isShiftContextReady]);

  useEffect(() => {
    void fetchLatestApprovedPlan();
  }, [customerId]);

  const activeLoadingIds = useMemo<string[]>(() => {
    if (!latestPlan?.planData?.schedule) return [];
    const ids = new Set<string>();
    latestPlan.planData.schedule.forEach((row) => {
      const loadingArea = row[9];
      if (loadingArea && String(loadingArea).startsWith('L')) {
        ids.add(String(loadingArea));
      }
    });
    return Array.from(ids);
  }, [latestPlan]);

  const activeDumpingIds = useMemo<string[]>(() => {
    if (!latestPlan?.planData?.schedule) return [];
    const ids = new Set<string>();
    latestPlan.planData.schedule.forEach((row) => {
      const dumpingArea = row[10];
      if (dumpingArea && String(dumpingArea).startsWith('D')) {
        ids.add(String(dumpingArea));
      }
    });
    return Array.from(ids);
  }, [latestPlan]);

  return (
    <CustomMuiThemeProvider>
      <Box
        display={'flex'}
        flexDirection={'column'}
        gap={2}
        py={3}
        px={1}
        position={'relative'}
      >
        <HeaderWithFilters
          tripsData={tripsData}
          date={date}
          handleDateChange={handleDateChange}
          loading={loading}
          selectedDateRange={selectedDateRange}
          currentShiftLabel={currentShiftLabel}
          availableShifts={availableShifts}
          selectedShiftNumber={selectedShiftNumber}
          onSelectedShiftChange={setSelectedShiftNumber}
        />
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 'calc(100vh - 200px)',
              width: '100%',
              backgroundColor: 'customBackground.section',
            }}
          >
            <SingleFadeLoader isFullScreen={false} message={t('cdp_loading')} />
          </Box>
        ) : (
          <Box display="flex" gap={2} alignItems="flex-start">
            {/* Left: scrollable plan content */}
            <Box
              display="flex"
              flexDirection="column"
              gap={4}
              flex={1}
              minWidth={0}
            >
              <ActivePlanCard
                selectedDateRange={selectedDateRange}
                date={date}
                handleDateChange={handleDateChange}
                currentShiftLabel={currentShiftLabel}
              />
              <HourlyMaterialComparisonChart
                tripsData={tripsData}
                selectedRange={selectedDateRange}
              />
              <KPICardAndChartWrapper
                tripsData={tripsData}
                selectedRange={selectedDateRange}
              />
              <Timeline
                tripsData={tripsData}
                selectedRange={selectedDateRange}
                date={date}
                currentShiftStartUtc={currentShiftStartUtc}
                currentShiftEndUtc={currentShiftEndUtc}
              />
            </Box>

            {/* Right: sticky map panel */}
            <Box
              sx={{
                width: 380,
                flexShrink: 0,
                position: 'sticky',
                top: 16,
                height: 'calc(100vh - 160px)',
              }}
            >
              <DispatchPlanMap
                loadingPoints={loadingPoints}
                dumpingPoints={dumpingPoints}
                activeLoadingIds={activeLoadingIds}
                activeDumpingIds={activeDumpingIds}
                enableDarkTheme={enableDarkTheme}
              />
            </Box>
          </Box>
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
};
